import { languageData, routes } from '@app/constant';
import LogoPng from '@assets/logo.png';
import LogoutPng from '@assets/Logout.png';
import BellPng from '@assets/Notifications Bell.png';
import { useAlert } from '@components/Alert/hooks/useAlert';
import { Button } from '@components/Button';
import { Language, LoaderType } from '@shared/enum';
import { getInitials, handleError } from '@shared/functions';
import { localStorageService } from '@shared/localStorageService';
import { useAppDispatch, useAppSelector } from '@state/configureStore';
import {
    disableLoading,
    enableLoading,
    selectBaseInfo,
    selectIsAuthenticated,
    selectUserRole,
    setIsAuthenticated,
    setLanguage,
} from '@state/pageSlice';
import { assignmentApi } from '@state/assignmentApi';
import { performanceApi } from '@state/performanceApi';
import { tournamentApi } from '@state/tournamentApi';
import { UserRole, useLogoutMutation, userApi } from '@state/userApi';
import { motion } from 'motion/react';
import { FC, useEffect } from 'react';
import { Badge, ButtonGroup, Container, Dropdown, Nav, Navbar, NavDropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import './Menu.scss';

export const Menu: FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const baseInfo = useAppSelector(selectBaseInfo);
    const userRole = useAppSelector(selectUserRole);
    const isLoginPage = location.pathname === '/login';
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const isAuthenticated = useAppSelector(selectIsAuthenticated);
    const { error } = useAlert();

    const isDriver = userRole === UserRole.DRIVER;

    const [
        logout,
        {
            data: validateLogoutData,
            error: errorLogout,
            isError: isErrorLogout,
            isSuccess: isSuccessLogout,
            isLoading: isLoadingLogout,
        },
    ] = useLogoutMutation();

    const changeLanguage = (key: Language) => {
        dispatch(setLanguage(key));
    };

    const performLogout = () => {
        logout({ language: baseInfo.language });
    };

    useEffect(() => {
        if (isLoadingLogout) {
            dispatch(enableLoading(LoaderType.logout));
        } else {
            dispatch(disableLoading(LoaderType.logout));
        }
    }, [isLoadingLogout, dispatch]);

    useEffect(() => {
        if (validateLogoutData && isSuccessLogout) {
            localStorageService.clear();
            dispatch(setIsAuthenticated(false));
            // Reset all API caches to prevent data leakage between users
            dispatch(userApi.util.resetApiState());
            dispatch(assignmentApi.util.resetApiState());
            dispatch(tournamentApi.util.resetApiState());
            dispatch(performanceApi.util.resetApiState());
            navigate(routes.login);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [validateLogoutData, isSuccessLogout, dispatch]);

    useEffect(() => {
        if (errorLogout && isErrorLogout) {
            const data = handleError(errorLogout);

            if (data.redirect) {
                navigate(routes.error500);
            } else if (data.msg) {
                error(data.msg);
            } else if (data.key) {
                error(t(data.key));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isErrorLogout, errorLogout, error, t]);

    return (
        <Navbar bg="light" variant="light" expand="lg" fixed="top" className="nav-container">
            <Container>
                <Navbar.Brand as={Link} to={routes.learning}>
                    <img src={LogoPng} alt="RoadMasterVr" height="50" className="d-inline-block align-top" />
                </Navbar.Brand>
                <Navbar.Toggle aria-controls="navbar-nav" />
                <Navbar.Collapse id="navbar-nav">
                    <Nav className="me-auto custom-nav">
                        {isAuthenticated && baseInfo.fullname && !isLoginPage && (
                            <>
                                <Nav.Link as={NavLink} to={routes.learning} className="position-relative">
                                    {t('page.learning')}
                                    {location.pathname === routes.learning ||
                                    location.pathname.startsWith(`${routes.learning}/`) ? (
                                        <motion.div
                                            layoutId="activeNavIndicator"
                                            className="nav-active-indicator"
                                            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                        />
                                    ) : null}
                                </Nav.Link>
                                {!isDriver && (
                                    <>
                                        <Nav.Link as={NavLink} to={routes.assignments} className="position-relative">
                                            {t('page.assignments')}
                                            {location.pathname === routes.assignments ||
                                            location.pathname.startsWith(`${routes.assignments}/`) ? (
                                                <motion.div
                                                    layoutId="activeNavIndicator"
                                                    className="nav-active-indicator"
                                                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                                />
                                            ) : null}
                                        </Nav.Link>
                                        <Nav.Link as={NavLink} to={routes.tournaments} className="position-relative">
                                            {t('page.tournaments')}
                                            {location.pathname === routes.tournaments ||
                                            location.pathname.startsWith(`${routes.tournaments}/`) ? (
                                                <motion.div
                                                    layoutId="activeNavIndicator"
                                                    className="nav-active-indicator"
                                                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                                />
                                            ) : null}
                                        </Nav.Link>
                                        <Nav.Link as={NavLink} to={routes.performance} className="position-relative">
                                            {t('page.performance')}
                                            {location.pathname === routes.performance ||
                                            location.pathname.startsWith(`${routes.performance}/`) ? (
                                                <motion.div
                                                    layoutId="activeNavIndicator"
                                                    className="nav-active-indicator"
                                                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                                />
                                            ) : null}
                                        </Nav.Link>
                                        <NavDropdown
                                            title={t('page.management')}
                                            id="management-nav-dropdown"
                                            className={`position-relative${location.pathname.startsWith('/management') ? ' active-dropdown' : ''}`}
                                        >
                                            <NavDropdown.Item as={NavLink} to={routes.managementUsers}>
                                                {t('page.managementUsers')}
                                            </NavDropdown.Item>
                                            <NavDropdown.Item as={NavLink} to={routes.managementGroups}>
                                                {t('page.managementGroups')}
                                            </NavDropdown.Item>
                                        </NavDropdown>
                                    </>
                                )}
                            </>
                        )}
                    </Nav>

                    <div className="flex-grow-1"></div>

                    <div className="d-flex flex-column flex-md-row gap-4">
                        {(!isAuthenticated || isLoginPage) && (
                            <Button
                                onClick={() => {
                                    window.location.href = 'https://www.roadmastervr.com/';
                                }}
                            >
                                {t('menu.buttons.product')}
                            </Button>
                        )}
                        {(!isAuthenticated || isLoginPage) && (
                            <Dropdown as={ButtonGroup} align="end">
                                <Dropdown.Toggle className="country-button">{baseInfo.language}</Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {Object.entries(languageData).map(([key, { label, shortLabel, img }]) => (
                                        <Dropdown.Item key={key} href="#" onClick={() => changeLanguage(shortLabel)}>
                                            <img src={img} alt={shortLabel} className="me-1" width={20} />
                                            {label}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        )}
                        {isAuthenticated && baseInfo.fullname && !isLoginPage && (
                            <>
                                <Link to={routes.settings} className="user-link d-flex align-items-center gap-1 me-2">
                                    <div className="d-flex align-items-center justify-content-center initials">
                                        {getInitials(baseInfo.fullname)}
                                    </div>
                                    {baseInfo.fullname}
                                </Link>
                                <Link
                                    className="d-flex align-items-center position-relative me-2 "
                                    to={routes.notifications}
                                >
                                    <div className="position-relative d-inline-block">
                                        <img src={BellPng} alt="Notifications" height={15} />
                                        <Badge
                                            bg="danger"
                                            pill
                                            className="notification position-absolute top-0 start-100 translate-middle"
                                        >
                                            0
                                        </Badge>
                                        <div className="flex-grow-1"></div>
                                    </div>
                                </Link>
                                <Link
                                    className="d-flex align-items-center"
                                    to={routes.login}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        performLogout();
                                    }}
                                >
                                    <img src={LogoutPng} alt="Logout" height={15} />
                                </Link>
                            </>
                        )}
                    </div>
                </Navbar.Collapse>
            </Container>
        </Navbar>
    );
};
